/**
 * Universities Listing Page
 * Shows all generated university pages with filtering by country
 */
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Search,
  GraduationCap,
  MapPin,
  TrendingUp,
  Users,
  ChevronRight,
  Loader2,
  Building2,
  Globe
} from 'lucide-react';

const API_URL = window.location.origin;

const UniversitiesListPage = () => {
  const { isDark } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || 'all');
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchUniversities();
  }, []);

  const fetchUniversities = async () => {
    try {
      // Fetch universities (500 is the API's max limit — covers all destinations)
      const res = await fetch(`${API_URL}/api/university-pages/public/list?limit=500`);
      if (res.ok) {
        const data = await res.json();
        setUniversities(data.universities || []);
        
        // Extract unique countries from live data, then merge with the
        // curated destination list so supported destinations (e.g. Malaysia,
        // Singapore) still show as filter options even when no universities
        // have been added yet.
        const liveCountries = new Set((data.universities || []).map(u => u.country).filter(Boolean));
        const curatedDestinations = [
          'Australia', 'Canada', 'Dubai', 'Finland', 'France', 'Germany',
          'Hungary', 'India', 'Ireland', 'Malaysia', 'New Zealand',
          'Singapore', 'UK', 'USA'
        ];
        curatedDestinations.forEach(c => liveCountries.add(c));
        setCountries([...liveCountries].sort());
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
    } finally {
      setLoading(false);
    }
  };

  // Normalize a country string to a canonical form so slug (e.g. "new-zealand")
  // or short/long names (e.g. "USA" vs "United States") all match the same
  // country records coming back from the API.
  const normalizeCountry = (value) => {
    if (!value) return '';
    const v = String(value).toLowerCase().replace(/-/g, ' ').trim();
    const aliases = {
      'usa': 'united states',
      'us': 'united states',
      'uk': 'united kingdom',
      'uae': 'united arab emirates',
      'dubai': 'united arab emirates',
    };
    return aliases[v] || v;
  };

  // Filter universities
  const filteredUniversities = universities.filter(uni => {
    const matchesSearch = !search || 
      uni.university_name?.toLowerCase().includes(search.toLowerCase()) ||
      uni.country?.toLowerCase().includes(search.toLowerCase());
    const matchesCountry = countryFilter === 'all' || 
      normalizeCountry(uni.country) === normalizeCountry(countryFilter);
    return matchesSearch && matchesCountry;
  });

  // Group by country for display
  const groupedByCountry = filteredUniversities.reduce((acc, uni) => {
    const country = uni.country || 'Other';
    if (!acc[country]) acc[country] = [];
    acc[country].push(uni);
    return acc;
  }, {});

  const handleCountryChange = (value) => {
    setCountryFilter(value);
    if (value === 'all') {
      searchParams.delete('country');
    } else {
      searchParams.set('country', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0d1117]' : 'bg-slate-50'}`}>
      <Header />
      
      {/* Hero Section */}
      <section className={`pt-24 pb-12 ${isDark ? 'bg-gradient-to-b from-[#1a2830] to-[#0d1117]' : 'bg-gradient-to-b from-white to-slate-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-gradient-to-r from-ux-cyan to-ux-blue text-white">
              <Globe className="w-3 h-3 mr-1" />
              {universities.length}+ Universities Worldwide
            </Badge>
            <h1 className={`text-4xl md:text-5xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Explore Universities Worldwide
            </h1>
            <p className={`text-lg ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
              Browse top-ranked institutions across the globe — partners and beyond. Find your perfect match.
            </p>
          </div>

          {/* Search & Filters */}
          <div className="mt-8 max-w-2xl mx-auto flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
              <Input
                placeholder="Search universities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`pl-12 h-12 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}
              />
            </div>
            <Select value={normalizeCountry(countryFilter) || 'all'} onValueChange={handleCountryChange}>
              <SelectTrigger className={`w-full sm:w-[180px] h-12 rounded-xl ${isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-slate-200'}`}>
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map(country => (
                  <SelectItem key={country} value={country.toLowerCase()}>{country}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Universities List */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-ux-cyan' : 'text-ux-blue'}`} />
            </div>
          ) : filteredUniversities.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                No universities found
              </h3>
              <p className={isDark ? 'text-white/60' : 'text-slate-500'}>
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedByCountry).sort().map(([country, unis]) => (
                <div key={country}>
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className={`text-2xl font-bold font-display ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {country}
                    </h2>
                    <Badge variant="outline" className={isDark ? 'border-white/20 text-white/70' : ''}>
                      {unis.length} universities
                    </Badge>
                  </div>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {unis.map((uni) => (
                      <Link
                        key={uni.slug}
                        to={`/universities/${uni.slug}`}
                        className={`group block rounded-2xl p-6 transition-all hover:scale-[1.02] ${
                          isDark 
                            ? 'bg-white/5 border border-white/10 hover:border-ux-cyan/50' 
                            : 'bg-white border border-slate-200 hover:border-ux-blue/50 shadow-sm hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            {uni.world_ranking && (
                              <Badge className={`mb-2 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                                Rank #{uni.world_ranking}
                              </Badge>
                            )}
                            <h3 className={`font-semibold text-lg group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {uni.university_name}
                            </h3>
                          </div>
                          <ChevronRight className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isDark ? 'text-white/40' : 'text-slate-400'}`} />
                        </div>
                        
                        <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-white/60' : 'text-slate-500'}`}>
                          {/* Acceptance rate removed — no verified source available */}
                          {uni.international_students && (
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              {uni.international_students}
                            </span>
                          )}
                        </div>
                        
                        {uni.hero_tagline && (
                          <p className={`mt-3 text-sm line-clamp-2 ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
                            {uni.hero_tagline}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className={`text-2xl md:text-3xl font-bold font-display mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Not sure which university is right for you?
          </h2>
          <p className={`mb-8 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
            Let our AI-powered tool analyze your profile and recommend the best universities for you.
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-ux-cyan to-ux-blue hover:opacity-90 text-white rounded-full px-8"
            asChild
          >
            <Link to="/contact">
              <GraduationCap className="w-5 h-5 mr-2" />
              Talk to Our Counsellors
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UniversitiesListPage;
